/**
 * i18n Currency Formatting Utilities
 *
 * Provides locale-aware currency and number formatting for the eXp CA application.
 * Supports both Canadian (CAD) and US (USD) currencies with proper locale formatting.
 */
import { CountryCodeList } from '../types/countries';

export type SupportedCurrency = 'CAD' | 'USD';

export interface I18nCurrencyFormatterOptions {
  locale: string;
  currency?: SupportedCurrency;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Formats a number as currency according to the specified locale and currency.
 * 
 * @param value - The numeric value to format
 * @param locale - The locale to use (e.g., 'en', 'en-CA', 'fr', 'fr-CA')
 * @param currency - The currency code ('CAD' or 'USD')
 * @param options - Additional formatting options
 * @returns Formatted currency string
 * 
 * @example
 * formatI18nCurrency(1234567, 'en-CA', 'CAD') // "CA$1,234,567"
 * formatI18nCurrency(1234567, 'fr-CA', 'CAD') // "1 234 567 $ CA"
 * formatI18nCurrency(1234567.89, 'en-CA', 'CAD', { maximumFractionDigits: 2 }) // "CA$1,234,567.89"
 */
export function formatI18nCurrency(
  value: number,
  locale: string = 'en',
  currency: SupportedCurrency = 'CAD',
  options?: Partial<I18nCurrencyFormatterOptions>
): string {
  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 0,
  };

  return new Intl.NumberFormat(locale, formatOptions).format(value);
}

/**
 * Formats a number with locale-specific thousand separators and decimal points.
 * 
 * @param value - The numeric value to format
 * @param locale - The locale to use
 * @param options - Additional formatting options
 * @returns Formatted number string
 * 
 * @example
 * formatI18nNumber(1234567, 'en-CA') // "1,234,567"
 * formatI18nNumber(1234567, 'fr-CA') // "1 234 567"
 * formatI18nNumber(1234567.89, 'en-CA', { maximumFractionDigits: 2 }) // "1,234,567.89"
 */
export function formatI18nNumber(
  value: number,
  locale: string = 'en',
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  const formatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 0,
  };
  
  return new Intl.NumberFormat(locale, formatOptions).format(value);
}

/**
 * Formats a price using compact notation (e.g., 1.5M, 500K).
 * Only shows decimal places when the number doesn't divide evenly.
 * 
 * @param value - The numeric value to format
 * @param locale - The locale to use
 * @param currency - The currency code (CAD or USD)
 * @returns Formatted compact price string
 * 
 * @example
 * formatI18nCompactPrice(1500000, 'en-CA', 'CAD') // "CA$1.5M"
 * formatI18nCompactPrice(800000, 'en-CA', 'CAD') // "CA$800K"
 * formatI18nCompactPrice(500000, 'fr-CA', 'CAD') // "500 k$ CA"
 * formatI18nCompactPrice(5000, 'en-CA', 'CAD') // "CA$5K"
 */
export function formatI18nCompactPrice(
  value: number,
  locale: string = 'en',
  currency: SupportedCurrency = 'CAD'
): string {
  // Determine if we need decimal places
  // Check if the value divides evenly into thousands or millions
  const needsDecimals = (value % 1000 !== 0 && value < 1000000) || 
                        (value % 1000000 !== 0 && value >= 1000000);

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: needsDecimals ? 1 : 0,
  }).format(value);
}

/**
 * Parses a formatted currency string back to a number.
 * Removes currency symbols, thousand separators, and other formatting.
 * 
 * @param formattedValue - The formatted currency string
 * @returns Numeric value or null if invalid
 * 
 * @example
 * parseI18nCurrency("CA$1,234,567") // 1234567
 * parseI18nCurrency("1 234 567 $ CA") // 1234567
 * parseI18nCurrency("$1.5M") // null (compact notation not supported)
 */
export function parseI18nCurrency(formattedValue: string): number | null {
  if (!formattedValue) return null;
  
  // Remove all non-numeric characters except decimal point and minus sign
  const cleaned = formattedValue.replace(/[^\d.-]/g, '');
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Determines the appropriate currency based on locale.
 *
 * @param locale - The locale string
 * @returns 'CAD' for Canadian locales, 'USD' otherwise
 *
 * @example
 * getCurrencyFromLocale('en-CA') // 'CAD'
 * getCurrencyFromLocale('fr-CA') // 'CAD'
 * getCurrencyFromLocale('en') // 'USD'
 */
export function getCurrencyFromLocale(locale: string): SupportedCurrency {
  // TODO: Need to use a more systematized approach to determine the currency based on the locale.
  return locale.includes(CountryCodeList.CANADA) || locale.includes(CountryCodeList.CANADA.toLowerCase()) ? 'CAD' : 'USD';
}
